import type { IContact } from '../../libs/contact';
import type { IAlert } from '../../libs/alert';
import type { IPatient } from '../../libs/patient';
import type { ISchedule } from '../../libs/schedule';
import type { AppKey } from '../../libs/apps';
import type { BaseResponse, PaginatedParams } from '../../libs/request';
import type { DataSetter } from '../../libs/hooks/useRequestJSON';
import { ITask } from '../../libs/tasks';
import { ICustomProgram } from '../../libs/composer';

/**
 * Enum for Triage Categories
 * @readonly
 * @enum {number}
 */
export enum TriageCategory {
  /** @member {number} */
  /** No category at all, default color  */
  None = 0,

  /** @member {number} */
  /** Clinical category: High-Risk, red patient  */
  Danger = 1,

  /** @member {number} */
  /** Clinical category: Medium-Risk, yellow patient */
  Warning = 2,

  /** @member {number} */
  /** Clinical category: Low-Risk, green patient */
  Success = 3,

  /** @member {number} */
  /** Responded less than ${threshold} of their sessions */
  Unresponsive = 4,

  /** @member {number} */
  /** No responses to any sessions recorded */
  Empty = 5,

  /** @member {number} */
  /** No valid waiver, not consent recorded to use CareSignal */
  Inactive = 6,

  /** @member {number} */
  /** Issues snoozed for a set amount of time */
  Snoozed = 7,

  /** @member {number} */
  /** Patient's programs have reached their scheduled end date */
  Completed = 8,

  /** @member {number} */
  /** Patient's service is paused for an indefinite amount of time */
  Paused = 9,

  /** @member {number} */
  /** Patient was recently prescribed a CareSignal program */
  Recent = 10,

  /** @member {number} */
  /** Patient's programs have all been canceled by users */
  Canceled = 11,

  /** @member {number} */
  /** Subcategory: Archived */
  Archived = 100,

  /** @member {number} */
  /** Subcategory: Revoked */
  Revoked = 101
}

export const CARDINAL_CATEGORIES = [
  TriageCategory.Success,
  TriageCategory.Warning,
  TriageCategory.Danger
];

export type PatientCategory =
  | 'danger'
  | 'warning'
  | 'success'
  | 'unresponsive'
  | 'empty'
  | 'inactive'
  | 'snoozed'
  | 'completed'
  | 'paused'
  | 'recent';

export type CategoryString =
  | 'alerts'
  | 'snoozedAlerts'
  | 'danger'
  | 'warning'
  | 'success'
  | 'unresponsive'
  | 'empty'
  | 'inactive'
  | 'snoozed'
  | 'completed'
  | 'paused'
  | 'recent';

export const MutexCategories: CategoryString[] = [
  'danger',
  'warning',
  'success',
  'unresponsive',
  'inactive',
  'empty',
  'completed'
];

export const DualCategories: CategoryString[] = ['snoozed', 'paused', 'recent'];

export type OrderKeys =
  | 'alerts'
  | 'addressedAt'
  | 'snoozedUntil'
  | 'lastName'
  | 'enrollDate'
  | `clinical:${string}`;

export type OrderType = 'asc' | 'desc';

/**
 * /triage/data/ request types
 */

export interface PatientGroupParams {
  apps?: AppKey[];
  contactIds?: number[];
  tagIds?: number[];
}

export interface TriageParams {
  start: string;
  options: TriageParamsOptions;
  contactIds?: number[];
  tagIds?: number[];
}

export interface TriageParamsOptions {
  patients: {
    [key in PatientCategory]?: PatientPageOptions;
  };
  patientIds: {
    apps?: AppKey[];
  };
  alerts: AlertPageOptions;
  snoozedAlerts: PageOptions;
  staleAlerts?: BasicPageOptions;
  _tempAppFilter?: string;
  _forceAllApps?: boolean;
}

export interface BasicPageOptions extends PaginatedParams {
  apps?: AppKey[];
}

interface PageOptions extends BasicPageOptions {
  order?: OrderType;
}

interface AlertPageOptions extends PageOptions {
  unreadOnly?: boolean;
}

export interface PatientPageOptions extends BasicPageOptions {
  order?: [OrderKeys, OrderType];
}

export interface TriageResponse extends BaseResponse {
  total: number;
  triageInProgress?: boolean;
  contacts: IContact[];
  patientIds: number[];
  alerts: PaginatedResults<IAlert>;
  patients: Partial<Record<CategoryString, PaginatedResults<TriagedPatient>>>;
  snoozedAlerts: PaginatedResults<IAlert>;
}

export interface PaginatedResults<T> {
  page: number;
  limit: number;
  total: number;
  stunTotal?: number;
  results: T[];
  patientIds: any;
}

export interface TriagedPatient {
  patient: IPatient;
  alerts: IAlert[];
  category: TriageCategory;
  apps: {
    [key in AppKey]?: TriagedApp;
  };
}

export interface TriagedApp {
  alert: {
    total: number;
    unread: number;
    active: number;
  };
  category: TriageCategory;
  contactId: number;
  customProgram?: ICustomProgram;
  engagement: {
    responded: number;
    total: number;
    rate: number;
  };
  clinicalMetrics?: Record<
    string,
    {
      id: string;
      label: string;
      shortLabel: string;
      description: string;
      aggregationWindowDays: number;
      displayPrecision: number;
      valueType: 'number';
      value: number;
      display: string;
      unit?: string;
    }
  >;
  meta?: any;
  schedule?: ISchedule;
  endedSchedule?: ISchedule;
  statuses?: Array<string | { key: string; value: string | number }>;
  waiver?: {
    name?: AppKey;
    isConfirmed?: boolean;
    confirmedAt: Date;
    isRevoked?: boolean;
    revokedAt?: Date;
  };
}

/**
 * /triage/data_single/ request types
 */

export interface TriagePatientValue {
  updatePatientId: (id: number) => void;
  patientData: TriagedPatient;
  isPatientLoading?: boolean;
}

export interface TriagePatientParams {
  id: number;
  tz: string;
  start: string;
  includePastPrograms: boolean;
  end?: string;
}

export interface TriagePatientResponse extends BaseResponse {
  total: number;
  data: TriagedPatient;
  contacts: IContact[];
}

/**
 * /triage/data_statuses/ request types
 */

export interface TriageStatusesValue {
  triageStatuses: any;
}

export interface TriageStatusesResponse extends BaseResponse {
  statuses: any;
}

export interface TriageStatusesData {
  statuses: any;
}

/**
 * Transformed triage data types
 */

export interface Page {
  page: number;
  limit: number;
  cursor: {
    startId: number;
    endId: number;
  };
  total: number;
  stunTotal?: number;
  entityIds: number[];
}

export interface Triage {
  primaryCategoryId: number;
  secondaryCategoryIds: number[];
  patientId: number;
  alertIds: number[];
  apps: Record<string, TriagedApp>;
}

export interface TriageStats {
  totalAlerts: number;
  totalSnoozedAlerts: number;
  totalStaleAlerts: number;
  triageAlerts: number;
  danger: number;
  warning: number;
  success: number;
  unresponsive: number;
  inactive: number;
  completed: number;
  snoozed: number;
  paused: number;
  recent: number;
  stun: number;
}

export interface TriageData {
  pages: Partial<Record<CategoryString, Page>>;
  triages: Record<string, Triage>;
  patients: Record<string, IPatient>;
  alerts: Record<string, IAlert>;
  contacts: Record<string, IContact>;
  patientIds: number[];
  totalPatients: number;
  triageInProgress: boolean;
  patientIdsPerCategory: any;
}

export type TriageLoadingKey = CategoryString | 'patientGroup';

export type TriageLoadingState = Partial<Record<TriageLoadingKey, boolean>>;

export interface TriageValue {
  data: TriageData;
  triageInProgress: boolean;
  setTriageData: (data: TriageData | DataSetter<TriageData>) => void;
  addTriagedPatient: (data: TriagedPatient, contacts?: IContact[]) => void;
  staleAlertsData?: Page;
  options: TriageParamsOptions;
  updateOptions: (options: TriageParamsOptions) => void;
  tempAppFilter?: string;
  updateTempAppFilter?: (key: string) => void;
  patientId?: number;
  updateStaleAlertsPage: (page: number) => void;
  isLoading: boolean;
  loadingCategories?: TriageLoadingKey[];
  isCategoryLoading: (category: TriageLoadingKey) => boolean;
  isPendingPGEnterprise: boolean;
  isStaleAlertsLoading: boolean;
  error: any;
  stats: TriageStats;
  getContact: (id: number) => IContact;
  handleResolveAlert: (alerts: IAlert[]) => void;
  handleToggleSnoozeAlert: (
    alerts: IAlert[],
    hasUnsnoozed?: boolean,
    stunAlertIds?: IAlert['id'][]
  ) => void;
  handleToggleSnoozePatient: (
    patient: IPatient,
    alerts: IAlert[],
    hasUnsnoozed?: boolean
  ) => void;
  resetToDashboard?: () => void;
}

export interface TriageUpdaterValue {
  handleUpdatePatient: (patient: IPatient) => void;
  handleResolvePatient: (patient: IPatient) => void;
  handleToggleArchivePatient: (patient: IPatient) => void;
  handleTogglePausePatient: (
    patient: IPatient,
    cancelSuspension?: boolean,
    tasks?: ITask[],
    pausedApps?: string[]
  ) => void;
  handleMarkSeen: (patient: IPatient) => void;
  handlePushMessage: (patient: IPatient, alert?: IAlert) => void;
  handleUpdateSchedule: (
    patient: IPatient,
    app: AppKey,
    schedule: ISchedule
  ) => void;
}
